import {format} from 'date-fns';

import {FormButtonWrapper} from '../../../_old/Button/ButtonWrapper';
import {FormLevelError} from '../../../components/Error/FormLevelError';
import {MultiTextWrapper} from '../../../components/TextInput';
import {useAppForm} from '../../../hooks/useAppForm';
import {MAX_NOTE_LENGTH} from '../utils/constants';
import {
  getMinimumExpiryDate,
  getMaximumExpiryDate,
} from '../utils/expiryFunctions';
import {createLinkSchema} from '../utils/schemas/link';

import type {ReactFormSubmitHandler} from '../../../lib/react-form';
import type {CreateLinkFormValues} from '../types/forms';

type Props = {
  initialValues: CreateLinkFormValues;
  handleSubmit: ReactFormSubmitHandler<CreateLinkFormValues>;
  handleCancel: () => void;
  formError: Error | null;
};
export function LinkForm(props: Props): React.JSX.Element {
  const form = useAppForm({
    defaultValues: props.initialValues,
    onSubmit: props.handleSubmit,
    validators: {
      onSubmit: createLinkSchema,
    },
  });

  return (
    <div className="space-y-8">
      <form.Field
        name="emails"
        mode="array"
        children={(field) => (
          <MultiTextWrapper
            label="Emails"
            onAddValue={() => field.pushValue('')}
            required
          >
            {field.state.value.map((_, index) => (
              <form.AppField
                key={index}
                name={`emails[${index}]`}
                validators={{onBlur: createLinkSchema.shape.emails.element}}
                children={(subField) => (
                  <subField.FormMultiTextInput
                    onRemoveValue={() => field.removeValue(index)}
                    disabled={
                      form.state.isSubmitting ||
                      form.state.values.emails.length <= 1
                    }
                  />
                )}
              />
            ))}
          </MultiTextWrapper>
        )}
      />

      <form.AppField
        name="expiry"
        validators={{
          onBlur: createLinkSchema.shape.expiry,
        }}
        children={(field) => (
          <field.FormTextInput
            type="date"
            label="Expiry"
            required
            min={format(getMinimumExpiryDate(), 'yyyy-MM-dd')}
            max={format(getMaximumExpiryDate(), 'yyyy-MM-dd')}
          />
        )}
      />

      <form.AppField
        name="permissions.reports"
        validators={{
          onBlur: createLinkSchema.shape.permissions.shape.reports,
        }}
        children={(field) => (
          <field.FormCheckbox
            label="Enable access to reports"
            description="Recipients will be able to view all existing reports"
          />
        )}
      />

      <form.AppField
        name="permissions.presentations"
        validators={{
          onBlur: createLinkSchema.shape.permissions.shape.presentations,
        }}
        children={(field) => (
          <field.FormCheckbox
            label="Enable access to presentations"
            description="Recipients will be able to view all existing presentations"
          />
        )}
      />

      <form.AppField
        name="permissions.downloads"
        validators={{
          onBlur: createLinkSchema.shape.permissions.shape.downloads,
        }}
        children={(field) => (
          <field.FormCheckbox
            label="Enable access to file downloads"
            description="Recipients will be able to download all existing files downloads"
          />
        )}
      />

      <form.AppField
        name="note"
        validators={{
          onBlur: createLinkSchema.shape.note,
        }}
        children={(field) => (
          <field.FormTextareaInput
            label="Note"
            hint={`${field.state.value.length} of ${MAX_NOTE_LENGTH} characters`}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Include a note in the email to the recipients"
          />
        )}
      />

      <FormLevelError error={props.formError} />

      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.isPristine,
          state.isSubmitting,
        ]}
        children={([canSubmit, isPristine, isSubmitting]) => (
          <FormButtonWrapper isSubmitting={isSubmitting}>
            <button
              type="submit"
              className="btn-main"
              onClick={form.handleSubmit}
              disabled={isPristine || !canSubmit}
            >
              Submit
            </button>
            <button
              type="button"
              className="btn-muted"
              onClick={props.handleCancel}
            >
              Cancel
            </button>
          </FormButtonWrapper>
        )}
      />
    </div>
  );
}

LinkForm.displayName = 'LinkForm';
